// Shift lock — client side.
//
// The lock has to survive a page refresh (a cashier walking away shouldn't be
// undone by F5) and it has to work on a till that is offline, so the flag
// lives in localStorage and the PIN can be checked two ways:
//   online  → POST /auth/shift-unlock (authoritative, server-side lockout)
//   offline → PBKDF2 verifier cached at sign-in, checked with WebCrypto
// See backend/services/shift_lock.py for why that's an acceptable trade.

const LOCK_KEY = 'pharmxai_shift_locked';
const VERIFIER_KEY = 'pharmxai_shift_verifier';
const FAILS_KEY = 'pharmxai_shift_fails';

export const MAX_OFFLINE_FAILS = 5;

export const isShiftLocked = () => localStorage.getItem(LOCK_KEY) === '1';

export const setShiftLocked = (locked) => {
  if (locked) localStorage.setItem(LOCK_KEY, '1');
  else {
    localStorage.removeItem(LOCK_KEY);
    localStorage.removeItem(FAILS_KEY);
  }
};

export const cacheVerifier = (verifier) => {
  if (verifier) localStorage.setItem(VERIFIER_KEY, JSON.stringify(verifier));
  else localStorage.removeItem(VERIFIER_KEY);
};

export const clearShiftLockState = () => {
  localStorage.removeItem(LOCK_KEY);
  localStorage.removeItem(VERIFIER_KEY);
  localStorage.removeItem(FAILS_KEY);
};

const hex = (buffer) => [...new Uint8Array(buffer)].map((b) => b.toString(16).padStart(2, '0')).join('');
const bytes = (hexString) => new Uint8Array((hexString.match(/../g) || []).map((h) => parseInt(h, 16)));

export const verifyPinOffline = async (pin) => {
  const raw = localStorage.getItem(VERIFIER_KEY);
  if (!raw || !window.crypto?.subtle) return false;
  const verifier = JSON.parse(raw);
  const key = await window.crypto.subtle.importKey(
    'raw', new TextEncoder().encode(pin), 'PBKDF2', false, ['deriveBits'],
  );
  const derived = await window.crypto.subtle.deriveBits(
    { name: 'PBKDF2', salt: bytes(verifier.salt), iterations: verifier.iterations, hash: 'SHA-256' },
    key, 256,
  );
  return hex(derived) === verifier.hash;
};

// Offline attempts are counted locally; running out ends the session, so a
// stranger can't sit at an offline till and work through 10,000 PINs.
export const bumpOfflineFail = () => {
  const fails = Number(localStorage.getItem(FAILS_KEY) || 0) + 1;
  localStorage.setItem(FAILS_KEY, String(fails));
  return fails;
};
